import * as THREE from 'three';

export class FollowCamera {
  camera: THREE.PerspectiveCamera;

  /** Offset behind/above the kart (x=right, y=up, z=behind) */
  private offset = new THREE.Vector3(0, 6, -12);
  /** Point ahead of the kart to look at */
  private lookOffset = new THREE.Vector3(0, 1, 4);
  /** How quickly the camera catches up (higher = snappier) */
  private smoothSpeed = 12;

  private desiredPosition = new THREE.Vector3();
  private lookTarget = new THREE.Vector3();

  // ── FOV interpolation ──
  private static readonly BASE_FOV = 60;
  private static readonly MAX_SPEED_FOV = 70;
  private static readonly BOOST_FOV = 75;
  private currentFov = FollowCamera.BASE_FOV;

  // ── Camera shake ──
  private shakeTimer = 0;
  private shakeDuration = 0;
  private shakeIntensity = 0;
  private shakeOffset = new THREE.Vector3();

  constructor(aspect: number) {
    this.camera = new THREE.PerspectiveCamera(60, aspect, 0.1, 500);
    this.camera.position.set(0, 20, -30);
    this.camera.lookAt(0, 0, 0);
  }

  update(targetX: number, targetZ: number, targetYaw: number, dt: number, targetY: number = 0): void {
    const cosYaw = Math.cos(targetYaw);
    const sinYaw = Math.sin(targetYaw);

    const rightX = sinYaw;
    const rightZ = -cosYaw;

    this.desiredPosition.set(
      targetX + cosYaw * this.offset.z + rightX * this.offset.x,
      this.offset.y + targetY,
      targetZ + sinYaw * this.offset.z + rightZ * this.offset.x
    );

    const t = 1 - Math.exp(-this.smoothSpeed * dt);
    this.camera.position.lerp(this.desiredPosition, t);

    this.lookTarget.set(
      targetX + cosYaw * this.lookOffset.z + rightX * this.lookOffset.x,
      this.lookOffset.y + targetY,
      targetZ + sinYaw * this.lookOffset.z + rightZ * this.lookOffset.x
    );

    this.camera.lookAt(this.lookTarget);
  }

  snapTo(targetX: number, targetZ: number, targetYaw: number): void {
    const cosYaw = Math.cos(targetYaw);
    const sinYaw = Math.sin(targetYaw);
    const rightX = sinYaw;
    const rightZ = -cosYaw;

    this.camera.position.set(
      targetX + cosYaw * this.offset.z + rightX * this.offset.x,
      this.offset.y,
      targetZ + sinYaw * this.offset.z + rightZ * this.offset.x
    );

    this.lookTarget.set(
      targetX + cosYaw * this.lookOffset.z + rightX * this.lookOffset.x,
      this.lookOffset.y,
      targetZ + sinYaw * this.lookOffset.z + rightZ * this.lookOffset.x
    );

    this.camera.lookAt(this.lookTarget);
  }

  // ── Dynamic FOV ──

  updateFOV(speed: number, maxSpeed: number, isBoosting: boolean, dt: number): void {
    const ratio = Math.min(Math.abs(speed) / maxSpeed, 1);
    let targetFov = FollowCamera.BASE_FOV +
      (FollowCamera.MAX_SPEED_FOV - FollowCamera.BASE_FOV) * ratio;
    if (isBoosting) {
      targetFov = Math.max(targetFov, FollowCamera.BOOST_FOV);
    }
    const fovSpeed = targetFov > this.currentFov ? 6 : 4;
    this.currentFov += (targetFov - this.currentFov) * (1 - Math.exp(-fovSpeed * dt));
    this.camera.fov = this.currentFov;
    this.camera.updateProjectionMatrix();
  }

  // ── Camera Shake ──

  shake(duration: number, intensity: number): void {
    this.shakeDuration = duration;
    this.shakeTimer = duration;
    this.shakeIntensity = intensity;
  }

  updateShake(dt: number): void {
    this.camera.position.sub(this.shakeOffset);
    this.shakeOffset.set(0, 0, 0);
    if (this.shakeTimer <= 0) return;
    this.shakeTimer -= dt;
    if (this.shakeTimer < 0) this.shakeTimer = 0;
    const progress = this.shakeTimer / this.shakeDuration;
    const amplitude = this.shakeIntensity * progress;
    this.shakeOffset.set(
      (Math.random() * 2 - 1) * amplitude,
      (Math.random() * 2 - 1) * amplitude,
      0
    );
    this.camera.position.add(this.shakeOffset);
  }

  resize(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }
}
